import pauseIcon from "@loot-os/icons/icons/pause";
import playIcon from "@loot-os/icons/icons/play";
import { type ReactNode, useEffect, useRef, useState } from "react";

import { Icon } from "../core/Icon.js";
import { Text } from "../core/Text.js";
import { cn } from "../foundations/cn.js";
import { FOCUS_RING } from "../foundations/interaction.js";
import { useImageFallback } from "../foundations/useImageFallback.js";
import { useMediaQuery } from "../foundations/useMediaQuery.js";

/** How long a slide shows before the next, while the carousel moves on by itself. */
const BANNER_SLIDE_MS = 7000;

/**
 * The surface behind a slide's words over art: solid from the slide's left edge to just past the
 * words, then fading out across the art over 6rem, so the words keep the surface's text contrast
 * on any art. The slide clips it.
 */
const WORDS_SCRIM =
  "before:absolute before:-inset-y-[100vh] before:-left-[100vw] before:-right-24 before:-z-10 before:bg-[linear-gradient(to_left,transparent,var(--color-bg-surface)_6rem)]";

export interface BannerCarouselSlide {
  id: string;
  /** A large title, drawn as an h2 (or `titleAs`), with `subtitle` under it. */
  title?: string;
  /** The title's level: `h1` when the slide is the page's heading (a hero drawn as a slide). */
  titleAs?: "h1" | "h2";
  subtitle?: string;
  /**
   * Art for screens 768px and up (4.5:1) and for phones (2:1), each filling the frame, and what it
   * says, read aloud ("" when it is decoration). Without art, the words show on the surface.
   */
  art?: { wideSrc: string; phoneSrc: string; alt: string };
  /** The slide's call to action: under its words, or over the art's lower left on a slide with none. */
  action?: ReactNode;
}

export interface BannerCarouselProps {
  /** Names the region: "Featured". */
  label: string;
  slides: readonly BannerCarouselSlide[];
  /** A slide's position, read with it: "2 of 3". */
  slideLabel?: (position: number, count: number) => string;
  /** A dot's name: "Show banner 2 of 3". */
  dotLabel?: (position: number, count: number) => string;
  /** The rotation button's name while the banners move on by themselves, and once stopped. */
  pauseLabel?: string;
  playLabel?: string;
  className?: string;
}

/**
 * Banners edge to edge, one at a time: each slide's art, its title and subtitle (on the surface,
 * over the art's left or on their own) or both, with its call to action under the words, moving on
 * by itself (the current dot fills as it waits) and by swipe or the dots. It holds still while
 * hovered, focused, off screen or in a hidden tab, stops for good from its Pause button, and never
 * moves by itself under reduced motion. A lone slide shows without dots.
 */
export function BannerCarousel({
  label,
  slides,
  slideLabel = (position, count) => `${position} of ${count}`,
  dotLabel = (position, count) => `Show banner ${position} of ${count}`,
  pauseLabel = "Pause banners",
  playLabel = "Play banners",
  className,
}: BannerCarouselProps) {
  const track = useRef<HTMLUListElement>(null);
  const reduce = useMediaQuery("(prefers-reduced-motion: reduce)");
  const [current, setCurrent] = useState(0);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [stopped, setStopped] = useState(false);
  const count = slides.length;

  // The current slide follows the scroll, whatever moved it: a swipe, a trackpad, a dot.
  useEffect(() => {
    const el = track.current;
    if (!el) return;
    let frame = 0;
    const read = () => {
      frame = 0;
      setCurrent(Math.round(el.scrollLeft / Math.max(1, el.clientWidth)));
    };
    const onScroll = () => {
      frame ||= requestAnimationFrame(read);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      el.removeEventListener("scroll", onScroll);
    };
  }, []);

  // Off screen or in a hidden tab, it waits.
  useEffect(() => {
    const el = track.current;
    if (!el) return;
    let offscreen = false;
    const update = () => setHidden(offscreen || document.hidden);
    const observer =
      typeof IntersectionObserver === "undefined"
        ? null
        : new IntersectionObserver(([entry]) => {
            offscreen = !(entry?.isIntersecting ?? true);
            update();
          });
    observer?.observe(el);
    document.addEventListener("visibilitychange", update);
    return () => {
      observer?.disconnect();
      document.removeEventListener("visibilitychange", update);
    };
  }, []);

  const show = (index: number) => {
    const el = track.current;
    if (!el) return;
    el.scrollTo({ left: index * el.clientWidth, behavior: reduce ? "auto" : "smooth" });
  };

  const moving = count > 1 && !reduce;
  const paused = stopped || hovered || focused || hidden;

  return (
    <section
      aria-roledescription="carousel"
      aria-label={label}
      // A size container, so a slide's frame is measured against the carousel's own width.
      className={cn("@container w-full", className)}
      onPointerEnter={(event) => {
        if (event.pointerType === "mouse") setHovered(true);
      }}
      onPointerLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false);
      }}
    >
      <ul
        ref={track}
        className="flex snap-x snap-mandatory overflow-x-auto overscroll-x-contain bg-bg-surface [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {slides.map((slide, index) => (
          <li
            key={slide.id}
            aria-roledescription="slide"
            aria-label={slideLabel(index + 1, count)}
            // A slide out of view keeps its link out of the tab order.
            inert={index !== current || undefined}
            // At least the art's frame (2:1, 9:2 from md, at most 22rem tall), and taller when the
            // slide's words need it; the row draws every slide at its tallest one's height.
            className="relative flex min-h-[50cqi] w-full shrink-0 snap-start overflow-hidden md:min-h-[min(22.2222cqi,22rem)]"
          >
            <SlideContent slide={slide} eager={index === 0} />
          </li>
        ))}
      </ul>
      {count > 1 && (
        <div className="flex items-center justify-center pt-1">
          {slides.map((slide, index) => {
            const active = index === current;
            return (
              <button
                key={slide.id}
                type="button"
                aria-label={dotLabel(index + 1, count)}
                aria-current={active ? "true" : undefined}
                onClick={() => show(index)}
                className={cn("group flex h-6 items-center rounded-pill px-1", FOCUS_RING)}
              >
                <span
                  className={cn(
                    "relative block h-2 overflow-hidden rounded-pill bg-border-strong transition-[width] duration-slow ease-standard motion-reduce:transition-none",
                    active ? "w-7" : "w-2 group-hover:bg-text-secondary",
                  )}
                >
                  {active && (
                    <span
                      // A new slide restarts the fill.
                      key={current}
                      className={cn(
                        "absolute inset-0 origin-left rounded-pill bg-accent-default",
                        moving && "animate-banner-progress",
                      )}
                      style={
                        moving
                          ? {
                              animationDuration: `${BANNER_SLIDE_MS}ms`,
                              animationPlayState: paused ? "paused" : "running",
                            }
                          : undefined
                      }
                      onAnimationEnd={moving ? () => show((current + 1) % count) : undefined}
                    />
                  )}
                </span>
              </button>
            );
          })}
          {/* Hover and focus hold it only while they last; this stops it until Play
              (WCAG 2.2.2). Play also lets go of the focus hold, so it moves on while focus
              stays here; focus on another of its controls holds it again. */}
          {moving && (
            <button
              type="button"
              aria-label={stopped ? playLabel : pauseLabel}
              onClick={() => {
                if (stopped) setFocused(false);
                setStopped(!stopped);
              }}
              className={cn(
                "flex size-6 items-center justify-center rounded-pill text-text-secondary hover:text-text-primary",
                FOCUS_RING,
              )}
            >
              <Icon icon={stopped ? playIcon : pauseIcon} size={12} />
            </button>
          )}
        </div>
      )}
    </section>
  );
}

/**
 * A slide's art and words. When its art is missing or fails, a slide with words shows them on the
 * store's surface, and a slide of art alone shows its description in their place, so no slide is
 * ever empty.
 */
function SlideContent({ slide, eager }: { slide: BannerCarouselSlide; eager: boolean }) {
  const art = useImageFallback(slide.art?.phoneSrc);
  const words = Boolean(slide.title || slide.subtitle || slide.action);
  return (
    <>
      {slide.art && !art.failed && (
        <picture className="absolute inset-0">
          <source media="(min-width: 48rem)" srcSet={slide.art.wideSrc} />
          <img
            src={slide.art.phoneSrc}
            alt={slide.art.alt}
            loading={eager ? "eager" : "lazy"}
            decoding="async"
            draggable={false}
            onError={art.onError}
            className="size-full object-cover"
          />
        </picture>
      )}
      {words ? (
        <SlideWords slide={slide} />
      ) : (
        slide.art &&
        art.failed &&
        slide.art.alt && <SlideWords slide={{ ...slide, subtitle: slide.art.alt }} />
      )}
    </>
  );
}

/**
 * A slide's title, subtitle and call to action, left in the page's gutter, on the surface where
 * they lie over art.
 */
function SlideWords({ slide }: { slide: BannerCarouselSlide }) {
  const words = Boolean(slide.title || slide.subtitle);
  return (
    <div
      className={cn(
        "relative mx-auto flex w-full max-w-[1440px] flex-col items-start px-4 py-6 md:px-8 md:py-8 lg:px-20 lg:py-10",
        // Words center in the frame; a call to action alone sits at the art's lower left.
        words ? "justify-center" : "pointer-events-none justify-end [&>*]:pointer-events-auto",
      )}
    >
      <div
        data-scrim={(words && slide.art) || undefined}
        className={cn(
          "relative isolate flex max-w-full flex-col items-start gap-3 md:gap-4",
          words && slide.art && WORDS_SCRIM,
        )}
      >
        {slide.title && (
          <Text
            as={slide.titleAs ?? "h2"}
            variant="display-xl3"
            tone="primary"
            className="max-w-[20ch] uppercase [overflow-wrap:anywhere] md:text-display-xl2"
          >
            {slide.title}
          </Text>
        )}
        {slide.subtitle && (
          <Text
            variant="body-b1"
            tone="secondary"
            className="max-w-[36ch] [overflow-wrap:anywhere] md:max-w-[44ch] md:text-heading-h2 md:font-normal"
          >
            {slide.subtitle}
          </Text>
        )}
        {slide.action && <div className={cn(words && "pt-1 md:pt-2")}>{slide.action}</div>}
      </div>
    </div>
  );
}
